/**
 * @file SaveDesignDialog.tsx
 * @description «Сохранить текущий вид как дизайн» и «Переименовать дизайн»
 * (phase19.16 Р1-6а; uiux-builder-shell.md §9; контракт §14.2): название
 * (до 60 знаков, как у сервера), откуда взять вид — черновик или как сейчас
 * на сайте (выбор есть, только если черновик отличается), галочка «вместе с
 * логотипами» (§7.16 фазы). Ошибки сервера — текстом в окне.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { CheckRow } from "../../site-view/CheckRow";
import { Segmented } from "../../site-view/Segmented";
import { TextField } from "../../site-view/TextField";
import { designErrorText } from "../../../hooks/site-builder/useDesigns";
import type { CreateSiteDesignInput } from "../../../lib/api-site-designs";
import { DESIGN_NAME_MAX } from "../../../lib/site-view/design-status";

interface Props {
  open: boolean;
  /** Переименование: прежнее название; нет — новый дизайн. */
  renameFrom?: string;
  /** Черновик оформления отличается от сайта — спросить, откуда брать вид. */
  draftDiffers: boolean;
  onClose: () => void;
  onSubmit: (input: CreateSiteDesignInput) => Promise<void>;
}

export function SaveDesignDialog({ open, renameFrom, draftDiffers, onClose, onSubmit }: Props) {
  const [name, setName] = useState("");
  const [source, setSource] = useState<"DRAFT" | "PUBLISHED">("DRAFT");
  const [withLogos, setWithLogos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rename = renameFrom !== undefined;

  useEffect(() => {
    if (!open) return;
    setName(renameFrom ?? "");
    setSource("DRAFT");
    setWithLogos(false);
    setError(null);
  }, [open, renameFrom]);

  async function submit() {
    if (!name.trim()) {
      setError("Назовите дизайн — например, «Лето» или «Новогодний».");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ name: name.trim(), source, withLogos });
      onClose();
    } catch (err) {
      setError(designErrorText(err, "Не удалось сохранить дизайн."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-[35rem]">
        <DialogHeader>
          <DialogTitle>{rename ? "Переименовать дизайн" : "Сохранить текущий вид как дизайн"}</DialogTitle>
          {rename ? null : (
            <DialogDescription>Цвета, шрифты, формы, фон и рамка, шапка и подвал — чтобы вернуть их в один шаг или применить позже.</DialogDescription>
          )}
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
          <TextField label="Название" value={name} max={DESIGN_NAME_MAX} placeholder="Лето" onChange={setName} />
          {!rename && draftDiffers ? (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-on-surface">Какой вид сохранить</span>
              <Segmented
                aria-label="Какой вид сохранить"
                options={[
                  { value: "DRAFT", label: "Черновик" },
                  { value: "PUBLISHED", label: "Как сейчас на сайте" },
                ]}
                value={source}
                onChange={setSource}
              />
            </div>
          ) : null}
          {rename ? null : (
            <CheckRow checked={withLogos} onChange={setWithLogos}>
              Вместе с логотипами
            </CheckRow>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {rename ? "Переименовать" : "Сохранить дизайн"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
